import { useEffect, useRef } from 'react';

import { fileReaderUtils } from '@pukka/common/utils';
import { proxy, subscribeKey } from '@pukka/valtio';

type FileStatus = 'uploading' | 'uploaderr' | 'valid' | 'error' | 'removed';

interface ImageUploadFileType<T> {
  data?: File;
  nonce: string;
  url: string;
  name: string;
  status: FileStatus;
  error?: string;
  meta?: T;
  progress?: number;
}

interface ImageUploadState<T> {
  files: Map<string, ImageUploadFileType<T>>;
  order: Set<string>;
  deletable: boolean;
  ordering: boolean;
  readonly isLimitReached: boolean;
  readonly count: number;
  onAdd(list: FileList): Promise<ImageUploadFileType<T>[]>;
  onRemove(nonce: string): Promise<void>;
  onRetryUpload(nonce: string): Promise<ImageUploadFileType<T>>;
  onReorder(nonce: string, direction: -1 | 1): void;
  useUpdate(values: Partial<ImageUploadFileType<T>>[] | undefined, deletable: boolean, ordering: boolean): void;
}

interface FactoryOption<T> extends Pick<ImageUploadState<T>, 'deletable' | 'ordering'> {
  readonly rule?: {
    errorMessage: string;
    maxCount?: number;
    maxWidth?: [width: number, errorMessage?: string];
    maxHeight?: [height: number, errorMessage?: string];
    maxSize?: [size: number, errorMessage?: string];
    minWidth?: [width: number, errorMessage?: string];
    minHeight?: [height: number, errorMessage?: string];
    minSize?: [size: number, errorMessage?: string];
    fileTypes?: [fileTypes: string[], errorMessage?: string];
  };
  readonly values?: (Omit<ImageUploadFileType<T>, 'status' | 'nonce'> & { nonce?: string; status?: FileStatus })[];
  readonly uploadHandler?: (
    image: ImageUploadFileType<T>,
    onProgress: (progress: number) => void,
    abortController: AbortController,
  ) => Promise<T>;
  readonly removeHandler?: (meta: T) => Promise<void>;
  readonly onUpdate?: (files: ImageUploadFileType<T>[], status: FileStatus, error?: string) => void;
}

const abortControllers: Record<string, AbortController> = {};

function updateValuesFnFactory<T>(State: ImageUploadState<T>, cache: { id: number }, values?: Partial<ImageUploadFileType<T>>[]) {
  return () => {
    values?.forEach(({ nonce, name, url, ...value }) => {
      if (nonce && name && url) {
        const file = State.files.get(nonce);

        if (file) {
          State.files.set(
            nonce,
            proxy({
              ...file,
              ...value,
              name,
              url,
            }),
          );
        } else {
          State.files.set(
            nonce,
            proxy({
              ...value,
              status: value.status ?? 'valid',
              nonce,
              url,
              name,
            }),
          );
        }
      } else if (name && url) {
        // Add into the map
        cache.id += 1;

        const _nonce = String(Date.now() + cache.id);

        State.files.set(
          _nonce,
          proxy({
            ...value,
            status: value.status ?? 'valid',
            nonce: _nonce,
            name,
            url,
          }),
        );

        State.order.add(_nonce);
      }
    });

    // Trigger change
    State.order = new Set(State.order);
    State.files = new Map(State.files);
  };
}

function updateDeletableFnFactory<T>(State: ImageUploadState<T>, deletable: boolean) {
  return () => {
    State.deletable = deletable;
  };
}

function updateOrderingFnFactory<T>(State: ImageUploadState<T>, ordering: boolean) {
  return () => {
    State.ordering = ordering;
  };
}

function getErrorSummary<T>(files: ImageUploadFileType<T>[]): [FileStatus, string?] {
  const [status, error] = files.reduce(
    (sum, file) => {
      if (sum?.[0] === 'error' || sum?.[0] === 'uploaderr' || sum?.[0] === 'uploading') {
        return sum;
      }

      return [file.status, file.error];
    },
    [] as [FileStatus?, string?],
  );

  return [status ?? 'valid', error];
}

function getError<T>(rule: FactoryOption<T>['rule'], width: number, height: number, size: number, type: string): string | false {
  if (!rule) {
    return false;
  }

  if (rule.fileTypes && !rule.fileTypes[0].includes(type)) {
    return rule.fileTypes[1] ?? rule.errorMessage;
  }

  if (rule.maxSize && size > rule.maxSize[0]) {
    return rule.maxSize[1] ?? rule.errorMessage;
  }

  if (rule.minSize && size < rule.minSize[0]) {
    return rule.minSize[1] ?? rule.errorMessage;
  }

  if (rule.maxWidth && width > rule.maxWidth[0]) {
    return rule.maxWidth[1] ?? rule.errorMessage;
  }

  if (rule.minWidth && width < rule.minWidth[0]) {
    return rule.minWidth[1] ?? rule.errorMessage;
  }

  if (rule.maxHeight && height > rule.maxHeight[0]) {
    return rule.maxHeight[1] ?? rule.errorMessage;
  }

  if (rule.minHeight && height < rule.minHeight[0]) {
    return rule.minHeight[1] ?? rule.errorMessage;
  }

  return false;
}

function abort(abortController: AbortController) {
  abortController.abort();
}

async function uploadFile<T>(
  file: File,
  State: ImageUploadState<T>,
  nonce: string,
  { rule, uploadHandler }: Pick<FactoryOption<T>, 'rule' | 'uploadHandler'>,
) {
  const { width, height, size, type, name, base64 } = await fileReaderUtils.getImageMetadata(file);
  const error = getError(rule, width, height, size, type);

  const fileData: ImageUploadFileType<T> = proxy({
    url: base64,
    name,
    nonce,
    status: error ? 'error' : 'uploading',
    error: error || undefined,
  });

  State.files.set(nonce, fileData);
  State.order.add(nonce);

  // Trigger change
  State.order = new Set(State.order);
  State.files = new Map(State.files);

  if (error) {
    return fileData;
  }

  abortControllers[nonce] = new AbortController();

  try {
    const meta = await new Promise<T | undefined>((res, rej) => {
      abortControllers[nonce]?.signal.addEventListener('abort', () => {
        rej(new Error('Network call aborted'));
      });

      if (uploadHandler) {
        uploadHandler(
          fileData,
          (number) => {
            fileData.progress = number;
          },
          abortControllers[nonce],
        )
          .then((result) => {
            return res(result);
          })
          .catch(rej);
      } else {
        res(undefined);
      }
    });

    fileData.meta = meta;
    fileData.status = 'valid';

    delete fileData.progress;
    delete fileData.error;
    delete abortControllers[nonce];
  } catch (err) {
    fileData.data = file;
    fileData.error = String(err);
    fileData.status = 'uploaderr';

    delete fileData.progress;
  } finally {
    // Trigger change
    State.order = new Set(State.order);
    State.files = new Map(State.files);
  }

  return fileData;
}

function imageUploadStateFactory<T>({ values, rule, deletable, ordering, uploadHandler, removeHandler, onUpdate }: FactoryOption<T>) {
  const stateCache = { id: 0 };
  const initialValues = values
    ?.filter((value) => value.url && value.name)
    .map((value) => {
      stateCache.id += 1;

      const nonce = value.nonce ?? String(Date.now() + stateCache.id);

      return [
        nonce,
        proxy({
          ...value,
          status: value.status ?? 'valid',
          nonce,
          url: value.url,
          name: value.name,
        }),
      ] as [string, ImageUploadFileType<T>];
    });

  const State = proxy<ImageUploadState<T>>({
    // We are not using proxyMap nor proxySet here
    // The reason being: the way we set progress on the ImageUploadFileType
    // If we use proxyMap/Set, each mutation on progress will trigger changes on the Map/Set
    // Therefore will trigger so much rerender on the component
    files: new Map(initialValues),
    order: new Set(initialValues?.map((i) => i[0]) ?? []),
    deletable,
    ordering,
    get isLimitReached() {
      return this.files.size >= (rule?.maxCount ?? Infinity);
    },
    get count() {
      return this.files.size;
    },
    async onRetryUpload(nonce: string): Promise<ImageUploadFileType<T>> {
      const data = State.files.get(nonce);

      if (!data || !data.data) {
        throw new Error('No file found');
      }

      return uploadFile(data.data, State, data.nonce, { rule, uploadHandler });
    },
    async onAdd(files): Promise<ImageUploadFileType<T>[]> {
      const errorNonces = [...State.files.values()].filter((file) => file.status === 'error').map((file) => file.nonce);

      // clear errors
      errorNonces.forEach((nonce) => {
        State.onRemove(nonce);
      });

      return Promise.all(
        Array(files.length)
          .fill(undefined)
          .map(async (u, i) => {
            stateCache.id += 1;

            const nonce = String(Date.now() + stateCache.id);

            return uploadFile(files[i], State, nonce, { rule, uploadHandler });
          }),
      );
    },
    async onRemove(nonce) {
      abortControllers[nonce]?.abort();
      delete abortControllers[nonce];

      const fileData = State.files.get(nonce);

      if (!fileData) {
        return;
      }

      State.files.delete(nonce);
      State.order.delete(nonce);

      // Trigger change
      State.order = new Set(State.order);
      State.files = new Map(State.files);

      fileData.status = 'removed';

      if (fileData.meta) {
        // Remove from cloud first
        await removeHandler?.(fileData.meta);
      }
    },
    async onReorder(nonce, direction) {
      const index = [...State.order].indexOf(nonce);

      if (index === -1) {
        return;
      }

      if (direction === -1 && index > 0) {
        const newOrder = [...State.order];

        // Move the image;
        newOrder.splice(index - 1, 0, newOrder.splice(index, 1)[0]);

        // Trigger change
        State.order = new Set(newOrder);

        return;
      }

      if (direction === 1 && index < State.files.size - 1) {
        const newOrder = [...State.order];

        // Move the image;
        newOrder.splice(index + 1, 0, newOrder.splice(index, 1)[0]);

        // Trigger change
        State.order = new Set(newOrder);
      }
    },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    useUpdate(updatedValues, updateDeletable, updateOrdering) {
      // useUpdate(updateValuesFnFactory(State, stateCache, updatedValues), [updatedValues]);
      // useUpdate(updateDeletableFnFactory(State, updateDeletable), [updateDeletable]);
      // useUpdate(updateOrderingFnFactory(State, updateOrdering), [updateOrdering]);

      useEffect(() => {
        // Subscribe for files and order changes
        const unsub = subscribeKey(State, 'files', (files) => {
          const { order } = State;
          const images = [...order].map((nonce) => files.get(nonce)).filter((image) => !!image) as ImageUploadFileType<T>[];

          const [status, error] = getErrorSummary(images);

          onUpdate?.(images, status, error);
        });

        return () => {
          // Abort all upload when dismounted
          Object.values(abortControllers).forEach(abort);

          unsub();
        };
      }, []);
    },
  });

  return State;
}

function useImageUploadState<T>(initialValue: FactoryOption<T>) {
  const ref = useRef<ImageUploadState<T>>();

  if (!ref.current) {
    ref.current = imageUploadStateFactory(initialValue);
  }

  return ref.current;
}

export {
  type ImageUploadFileType,
  type ImageUploadState,
  type FactoryOption as ImageUploadFactoryOption,
  imageUploadStateFactory as _imageUploadStateFactory,
  updateValuesFnFactory as _updateValuesFnFactory,
  updateDeletableFnFactory as _updateDeletableFnFactory,
  updateOrderingFnFactory as _updateOrderingFnFactory,
  getErrorSummary as _getErrorSummary,
  getError as _getError,
  uploadFile as _uploadFile,
  abort as _abort,
  useImageUploadState,
};
